// src/components/ClientDashboard.js

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuthContext } from '../context/AuthContext';
import {
  IoBarbell,
  IoNutrition,
  IoTrendingUp,
  IoCalendarOutline,
  IoFlameOutline,
  IoCheckmarkCircle,
  IoArrowForward,
  IoPlayCircle,
  IoPersonCircleOutline,
  IoSparklesOutline,
} from 'react-icons/io5';

// ── Helpers ─────────────────────────────────────────────────
const getDayName = () => {
  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  return days[new Date().getDay()];
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return '¡Buenos días';
  if (h < 18) return '¡Buenas tardes';
  return '¡Buenas noches';
};

const countTodayExercises = (program) => {
  if (!program?.routines) return 0;
  return program.routines.reduce((acc, r) =>
    acc + r.exercise_groups.reduce((a, g) => a + g.exercises.length, 0), 0);
};

// ── Sub-components ───────────────────────────────────────────
const QuickNavCard = ({ to, icon: Icon, title, subtitle, accent }) => (
  <Link to={to} className="group glass-panel rounded-xl p-5 flex items-center gap-4 hover:-translate-y-1 hover:border-white/20 transition-all duration-300">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${accent}`}>
      <Icon size={24} className="text-white" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-white text-base">{title}</p>
      <p className="text-xs text-gray-400 mt-0.5 truncate">{subtitle}</p>
    </div>
    <IoArrowForward size={18} className="text-gray-600 group-hover:text-white group-hover:translate-x-1 transition-all" />
  </Link>
);

// ── Main ─────────────────────────────────────────────────────
const ClientDashboard = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [program, setProgram] = useState(null);
  const [assignedCoach, setAssignedCoach] = useState(null);
  const [todayLogs, setTodayLogs] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);

  const firstName = user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'Atleta';

  useEffect(() => {
    const load = async () => {
      try {
        const [progRes, rewardsRes, coachRes] = await Promise.all([
          api.get('/me/training-program').catch(() => ({ data: null })),
          api.get('/me/rewards').catch(() => ({ data: [] })),
          api.get('/me/assigned-coach').catch(() => ({ data: null })),
        ]);

        api.get('/quotes/random').then(r => setQuote(r.data)).catch(() => {});

        const prog = progRes.data;
        setProgram(prog);
        setRewards(rewardsRes.data || []);
        setAssignedCoach(coachRes.data || null);

        // Load today's logs if there's a program
        if (prog?.program?.id) {
          const logsRes = await api.get(`/me/logs/today?program_id=${prog.program.id}`).catch(() => ({ data: [] }));
          setTodayLogs(logsRes.data || []);
        }

        // Load this month's calendar
        const today = new Date();
        const y = today.getFullYear();
        const m = String(today.getMonth() + 1).padStart(2, '0');
        const calRes = await api.get(`/me/calendar-events?start=${y}-${m}-01&end=${y}-${m}-31`).catch(() => ({ data: [] }));
        setCalendarEvents(calRes.data || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Stats derived from calendar
  const completedThisMonth = calendarEvents.filter(e => e.status === 'completed').length;
  const totalThisMonth = calendarEvents.length;
  const streakCount = rewards.length;

  // Today's training summary
  const totalExercises = countTodayExercises(program);
  const loggedExercises = todayLogs.length;
  const trainingProgress = totalExercises > 0 ? Math.round((loggedExercises / totalExercises) * 100) : 0;
  const trainingComplete = totalExercises > 0 && loggedExercises >= totalExercises;

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-40 rounded-2xl bg-white/5" />
        <div className="h-24 rounded-2xl bg-white/5" />
        <div className="grid grid-cols-2 gap-4">
          <div className="h-32 rounded-xl bg-white/5" />
          <div className="h-32 rounded-xl bg-white/5" />
        </div>
      </div>
    );
  }

  return (
    <div className="fade-up space-y-5 max-w-2xl">

      {/* ── Greeting ── */}
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight">
          {getGreeting()}, {firstName}! 👋
        </h1>
        <p className="text-gray-400 text-sm mt-1">{getDayName()} · {new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
      </div>

      {/* ── Motivational Quote ── */}
      {quote && (
        <div className="relative overflow-hidden glass-panel rounded-2xl border border-marizan-red/20 px-5 py-4">
          <IoSparklesOutline size={18} className="absolute top-3 right-4 text-marizan-red/40" />
          <p className="text-white/90 text-base font-medium leading-relaxed italic">
            “{quote.text}”
          </p>
          {quote.author && (
            <p className="text-marizan-red text-xs mt-2 font-semibold tracking-wide">— {quote.author}</p>
          )}
        </div>
      )}

      <div className="glass-panel rounded-xl p-4 border border-white/10">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Entrenador asignado</p>
        {assignedCoach ? (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-marizan-red/20 border border-marizan-red/40 flex items-center justify-center">
              <IoPersonCircleOutline size={22} className="text-marizan-red" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm">{assignedCoach.coach_name}</p>
              <p className="text-xs text-gray-400">{assignedCoach.coach_email}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400">Aún no tienes un entrenador asignado.</p>
        )}
      </div>

      {/* ── Today's Training Hero ── */}
      {program ? (
        <div
          className={`relative overflow-hidden rounded-2xl p-6 cursor-pointer transition-transform hover:-translate-y-0.5 ${
            trainingComplete
              ? 'bg-gradient-to-br from-green-900/60 to-green-800/30 border border-green-500/30'
              : 'bg-gradient-to-br from-marizan-red/30 to-red-900/20 border border-marizan-red/30'
          }`}
          onClick={() => navigate('/my-training')}
        >
          {/* Background accent */}
          <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white/3 -translate-y-12 translate-x-12" />
          <div className="absolute bottom-0 right-12 w-20 h-20 rounded-full bg-white/3 translate-y-8" />

          <div className="relative">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Entrenamiento de hoy</p>
                <h2 className="text-xl font-bold text-white">{program.program?.name || 'Mi Programa'}</h2>
                <p className="text-sm text-gray-300 mt-0.5">
                  {program.program?.routines?.length || 0} rutina{program.program?.routines?.length !== 1 ? 's' : ''} · {totalExercises} ejercicio{totalExercises !== 1 ? 's' : ''}
                </p>
              </div>
              {trainingComplete ? (
                <div className="flex items-center gap-2 bg-green-500/20 border border-green-500/40 text-green-300 text-sm font-semibold px-3 py-1.5 rounded-full">
                  <IoCheckmarkCircle size={16} /> Completado
                </div>
              ) : (
                <div className="bg-marizan-red/20 border border-marizan-red/40 p-3 rounded-xl">
                  <IoPlayCircle size={28} className="text-marizan-red" />
                </div>
              )}
            </div>

            {/* Progress bar */}
            {totalExercises > 0 && (
              <div>
                <div className="flex justify-between text-xs text-gray-400 mb-1.5">
                  <span>{loggedExercises}/{totalExercises} ejercicios registrados</span>
                  <span>{trainingProgress}%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${trainingComplete ? 'bg-green-500' : 'bg-marizan-red'}`}
                    style={{ width: `${trainingProgress}%` }}
                  />
                </div>
              </div>
            )}

            <button className={`mt-4 flex items-center gap-2 text-sm font-semibold ${trainingComplete ? 'text-green-300' : 'text-marizan-red'}`}>
              {trainingComplete ? 'Ver detalles' : 'Comenzar ahora'} <IoArrowForward size={16} />
            </button>
          </div>
        </div>
      ) : (
        <div className="glass-panel rounded-2xl p-6 text-center border-dashed">
          <IoBarbell size={36} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-300 font-medium">Sin programa activo</p>
          <p className="text-gray-500 text-sm mt-1">Tu coach aún no te ha asignado un programa</p>
        </div>
      )}

      {/* ── Monthly Stats ── */}
      <div className="grid grid-cols-3 gap-3">
        <div className="glass-panel rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-white">{completedThisMonth}</p>
          <p className="text-xs text-gray-400 mt-1">Sesiones este mes</p>
        </div>
        <div className="glass-panel rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-white">{totalThisMonth > 0 ? Math.round((completedThisMonth / totalThisMonth) * 100) : 0}<span className="text-sm font-normal">%</span></p>
          <p className="text-xs text-gray-400 mt-1">Adherencia</p>
        </div>
        <div className="glass-panel rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-yellow-400 flex items-center justify-center gap-1">
            <IoFlameOutline size={20} />{streakCount}
          </p>
          <p className="text-xs text-gray-400 mt-1">Logros</p>
        </div>
      </div>

      {/* ── Quick Nav ── */}
      <div className="space-y-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Acceso rápido</p>
        <QuickNavCard
          to="/my-training"
          icon={IoBarbell}
          title="Mi Entrenamiento"
          subtitle={program ? `${program.program?.routines?.length || 0} rutinas · registra tu progreso` : 'Sin programa asignado'}
          accent="bg-marizan-red/80"
        />
        <QuickNavCard
          to="/nutrition"
          icon={IoNutrition}
          title="Mi Nutrición"
          subtitle="Consulta tu plan de alimentación diario"
          accent="bg-green-600/80"
        />
        <QuickNavCard
          to="/calendar"
          icon={IoCalendarOutline}
          title="Calendario"
          subtitle={`${completedThisMonth} entrenamientos completados este mes`}
          accent="bg-blue-600/80"
        />
        <QuickNavCard
          to="/my-progress"
          icon={IoTrendingUp}
          title="Mi Progreso"
          subtitle="Visualiza tus avances y métricas"
          accent="bg-purple-600/80"
        />
      </div>

    </div>
  );
};

export default ClientDashboard;
